import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

import { computeFacePose, createEmptyPose } from "./face-pose";
import { computeFaceMetrics, createEmptyMetrics } from "./face-metrics";
import { createFitMapping, getCameraRig } from "./fit-mapping";
import { deriveAutoFit, NEUTRAL_FIT } from "./auto-fit";
import { normalizeGlasses } from "./normalize-model";
import { OCCLUDERS } from "./occluders";
import { QuaternionSmoother, Vector3Filter } from "./one-euro";

/**
 * How long the frames stay put after the face stops being reported.
 *
 * Detection misses the occasional frame. Hiding the glasses on each one makes
 * them strobe; holding the last pose for a few frames' worth of time covers
 * every miss short enough to be a miss rather than a departure. Past that we
 * treat the face as gone — and as newly arrived when it comes back, because
 * filter history describing where a head *used* to be makes the frames glide
 * in from that old spot rather than appear on the face.
 */
const FACE_HOLD_MS = 180;

/**
 * Longest frame delta the rotation smoother will honour.
 *
 * The smoother is frame-rate compensated, so a long stall (a backgrounded tab,
 * a GC pause) would resolve to "snap straight to target" and jerk the frame.
 */
const MAX_DELTA_S = 0.1;

/** Loaded GLBs, shared for the life of the page — a swap back is instant. */
const gltfCache = new Map();
const loader = new GLTFLoader();

function loadGltf(url) {
  let pending = gltfCache.get(url);
  if (!pending) {
    pending = loader.loadAsync(url);
    gltfCache.set(url, pending);
  }
  return pending;
}

/**
 * The whole three.js side of try-on: a mirrored camera feed painted as scene
 * geometry, a head-shaped depth occluder, and a normalized pair of glasses
 * anchored to the wearer's face.
 *
 * Deliberately outside React. `frame()` runs at camera rate and mutates the
 * scene graph directly, so tracking causes no state updates and no re-renders.
 */
export class TryOnRenderer {
  #container;
  #renderer;
  #scene;
  #camera;

  #backdrop = null;
  #videoTexture = null;

  #group;
  #occluderGeometry;
  #occluderMaterial;
  #model = null;
  #modelToken = 0;

  #mapping = null;
  #calibration = null;

  // Scratch objects, only ever written through — allocating per frame shows up
  // at 30-60 fps.
  #rig = {
    pose: createEmptyPose(),
    metrics: createEmptyMetrics(),
    positionFilter: new Vector3Filter(1.2, 0.02),
    widthFilter: new Vector3Filter(1, 0.01),
    // Face *shape* does not change, so this filters far harder than position:
    // every remaining wobble here is measurement noise.
    metricsFilter: new Vector3Filter(0.4, 0.005),
    rotationSmoother: new QuaternionSmoother(18),
    smoothedPosition: new THREE.Vector3(),
    smoothedWidth: new THREE.Vector3(),
    smoothedMetricsVector: new THREE.Vector3(),
    smoothedQuaternion: new THREE.Quaternion(),
    offset: new THREE.Vector3(),
  };

  #smoothedMetrics = createEmptyMetrics();
  #autoFit = null;
  #lastPoseAt = 0;
  #lastFrameAt = 0;

  constructor(container) {
    this.#container = container;

    this.#renderer = new THREE.WebGLRenderer({
      // Transparent so the plain <video> shows through until the backdrop
      // below takes over.
      alpha: true,
      antialias: true,
      powerPreference: "high-performance",
      stencil: false,
    });
    this.#renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.#renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.#renderer.setClearColor(0x000000, 0);
    this.#renderer.domElement.className = "vto-canvas";
    container.appendChild(this.#renderer.domElement);

    this.#scene = new THREE.Scene();
    this.#camera = new THREE.PerspectiveCamera(50, 1, 1, 100);

    this.#scene.add(new THREE.AmbientLight(0xffffff, 0.9));

    const key = new THREE.DirectionalLight(0xffffff, 1.6);
    key.position.set(0.5, 1, 2);
    this.#scene.add(key);

    const fill = new THREE.DirectionalLight(0xffffff, 0.5);
    fill.position.set(-1, 0.2, 1);
    this.#scene.add(fill);

    this.#group = new THREE.Group();
    this.#group.visible = false;
    this.#scene.add(this.#group);

    // Invisible stand-ins for the head and ears; see OCCLUDERS. One unit
    // sphere and one material between them — they differ only by transform.
    this.#occluderGeometry = new THREE.SphereGeometry(1, 32, 32);
    this.#occluderMaterial = new THREE.MeshBasicMaterial({
      colorWrite: false,
      depthWrite: true,
    });

    for (const { radii, centre } of OCCLUDERS) {
      const mesh = new THREE.Mesh(this.#occluderGeometry, this.#occluderMaterial);
      mesh.position.set(...centre);
      mesh.scale.set(...radii);
      // Ahead of the glasses so the depth they test against is already there,
      // and behind the backdrop, which writes no depth of its own.
      mesh.renderOrder = -1;
      this.#group.add(mesh);
    }
  }

  /**
   * The camera feed, drawn as scene geometry rather than left to the browser.
   *
   * A DOM <video> behind a transparent canvas is composited the instant a frame
   * decodes, while the glasses over it are drawn on the renderer's own schedule
   * from landmarks measured some milliseconds earlier. The two are never quite
   * the same moment, and the gap shows up as the frames sliding around the face
   * whenever the head moves and snapping back when it stops. Pulling the video
   * into the same draw call removes the gap by construction.
   */
  setVideo(video) {
    this.#disposeBackdrop();

    const texture = new THREE.VideoTexture(video);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = false;
    this.#videoTexture = texture;

    this.#backdrop = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      // No depth involvement at all: it is the background, and the occluder
      // behind it needs a clean depth buffer to write into. `toneMapped` off
      // keeps the feed looking like the camera rather than a grade.
      new THREE.MeshBasicMaterial({
        map: texture,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    this.#backdrop.renderOrder = -2;
    this.#scene.add(this.#backdrop);

    this.#applyBackdropSize();
  }

  /**
   * Rebuilds the landmark->world mapping and the camera rig.
   *
   * Call on every resize and whenever the camera changes resolution. The
   * filters are reset with it: their history is in the old units.
   */
  setLayout({ sourceSize, containerSize, mirrored, fit }) {
    const { width, height } = containerSize;
    if (!width || !height || !sourceSize.width || !sourceSize.height) return;

    this.#mapping = createFitMapping({
      sourceWidth: sourceSize.width,
      sourceHeight: sourceSize.height,
      containerWidth: width,
      containerHeight: height,
      mirrored,
      fit,
    });

    const { distance, fov } = getCameraRig(height);
    this.#camera.position.set(0, 0, distance);
    this.#camera.fov = fov;
    this.#camera.aspect = width / height;
    this.#camera.near = 1;
    this.#camera.far = distance * 4;
    this.#camera.updateProjectionMatrix();

    this.#renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.#renderer.setSize(width, height, false);

    if (this.#videoTexture) {
      // Flipped in the texture rather than by negating the mesh's scale, which
      // would reverse its winding order and cull the plane away.
      if (mirrored) {
        this.#videoTexture.wrapS = THREE.RepeatWrapping;
        this.#videoTexture.repeat.x = -1;
        this.#videoTexture.offset.x = 1;
      } else {
        this.#videoTexture.repeat.x = 1;
        this.#videoTexture.offset.x = 0;
      }
      this.#videoTexture.needsUpdate = true;
    }

    this.#applyBackdropSize();
    this.#resetFilters();
  }

  /** Swaps in a catalog frame, normalized to 1 unit wide and hung on the bridge. */
  async setFrame(frame) {
    const token = ++this.#modelToken;
    const gltf = await loadGltf(frame.modelUrl);
    // Another frame was picked while this GLB was still downloading.
    if (token !== this.#modelToken) return;

    if (this.#model) {
      this.#group.remove(this.#model);
      this.#model = null;
    }

    this.#model = normalizeGlasses(gltf.scene, frame.calibration);
    this.#calibration = frame.calibration;
    this.#group.add(this.#model);

    // Compile now rather than on the first frame the glasses are visible —
    // which is the exact moment a face was just found.
    this.#renderer.compile(this.#scene, this.#camera);
  }

  /**
   * One tracked frame: pose the glasses from the latest landmarks and draw.
   *
   * Called synchronously at the end of every detection, so the video texture
   * uploads the same camera frame the pose was measured from. Letting the two
   * loops find their own cadence is what makes frames appear to swim behind
   * the face during a head turn.
   */
  frame(landmarks) {
    if (!this.#mapping) return;

    const now = performance.now();
    const delta = this.#lastFrameAt
      ? Math.min((now - this.#lastFrameAt) / 1000, MAX_DELTA_S)
      : 1 / 60;
    this.#lastFrameAt = now;

    const held = this.#lastPoseAt !== 0 && now - this.#lastPoseAt < FACE_HOLD_MS;
    const pose = landmarks ? computeFacePose(landmarks, this.#mapping, this.#rig.pose) : null;

    if (!pose || !this.#model) {
      this.#group.visible = Boolean(this.#model) && held;
      this.#render();
      return;
    }

    // Back after a real absence, not a dropped frame. Whatever the filters
    // remember belongs to a head that has since left; keeping it makes the
    // frames slide across the picture to their new home.
    if (this.#lastPoseAt !== 0 && !held) this.#resetFilters();
    this.#lastPoseAt = now;

    const rig = this.#rig;
    rig.positionFilter.filter(pose.position, now, rig.smoothedPosition);
    rig.rotationSmoother.filter(pose.quaternion, delta, rig.smoothedQuaternion);

    // Filtered through the vector filter's x channel purely to reuse the same
    // adaptive smoothing; width jitters just like position does.
    rig.smoothedWidth.set(pose.faceWidth, 0, 0);
    rig.widthFilter.filter(rig.smoothedWidth, now, rig.smoothedWidth);
    const faceWidth = rig.smoothedWidth.x;

    const { widthRatio, offsetY: calY, offsetZ: calZ } = this.#calibration;

    // Measure the wearer, and let that measurement carry the catalog's
    // calibration — tuned on one face — across to a face shaped differently.
    const metrics = computeFaceMetrics(landmarks, this.#mapping, pose, rig.metrics);
    if (metrics) {
      rig.smoothedMetricsVector.set(
        metrics.eyeSpanRatio,
        metrics.eyeDropRatio,
        metrics.bridgeProtrusion,
      );
      rig.metricsFilter.filter(rig.smoothedMetricsVector, now, rig.smoothedMetricsVector);
      this.#smoothedMetrics.eyeSpanRatio = rig.smoothedMetricsVector.x;
      this.#smoothedMetrics.eyeDropRatio = rig.smoothedMetricsVector.y;
      this.#smoothedMetrics.bridgeProtrusion = rig.smoothedMetricsVector.z;

      this.#autoFit = deriveAutoFit(this.#smoothedMetrics, widthRatio);
    }

    const fit = this.#autoFit ?? NEUTRAL_FIT;

    // The model is normalized to 1 unit wide, so scale is just the target width.
    const width = faceWidth * widthRatio * fit.sizeScale;

    this.#group.quaternion.copy(rig.smoothedQuaternion);
    this.#group.scale.setScalar(width);

    // Offsets are expressed in frame-widths and applied along the head's own
    // axes, so they stay correct however the head is turned.
    rig.offset
      .set(0, (calY + fit.offsetY) * width, (calZ + fit.offsetZ) * width)
      .applyQuaternion(rig.smoothedQuaternion);
    this.#group.position.copy(rig.smoothedPosition).add(rig.offset);

    this.#group.visible = true;
    this.#render();
  }

  dispose() {
    this.#modelToken++;
    this.#disposeBackdrop();
    this.#occluderGeometry.dispose();
    this.#occluderMaterial.dispose();
    this.#renderer.dispose();
    this.#renderer.domElement.remove();
    // The glasses themselves keep their geometry and materials: they are
    // shared with the cached GLB, which outlives this renderer.
    this.#model = null;
    this.#container = null;
  }

  #render() {
    this.#renderer.render(this.#scene, this.#camera);
  }

  #applyBackdropSize() {
    if (!this.#backdrop || !this.#mapping) return;
    // The plane sits at z=0, where the camera rig maps world units 1:1 onto
    // container pixels, so this reproduces the CSS object-fit exactly and the
    // landmarks keep landing where they land in the picture.
    this.#backdrop.scale.set(this.#mapping.displayedWidth, this.#mapping.displayedHeight, 1);
  }

  #disposeBackdrop() {
    if (this.#backdrop) {
      this.#scene.remove(this.#backdrop);
      this.#backdrop.geometry.dispose();
      this.#backdrop.material.dispose();
      this.#backdrop = null;
    }
    if (this.#videoTexture) {
      this.#videoTexture.dispose();
      this.#videoTexture = null;
    }
  }

  #resetFilters() {
    this.#rig.positionFilter.reset();
    this.#rig.widthFilter.reset();
    this.#rig.metricsFilter.reset();
    this.#rig.rotationSmoother.reset();
  }
}
